<script setup>
import Navbar from "@/examples/Navbars/Navbar.vue";
import Sidenav from "@/examples/Sidenav/index.vue";
import { useStore } from "vuex";
import { computed, provide, useTemplateRef } from "vue";
import { useRoute } from "vue-router";
import { useIntervalFn } from "@vueuse/core";
import { useBreakpoints } from "@/composables/common/useBreakpoint";
import { useCompanyStore } from "@/storePinia/company";
import { storeToRefs } from "pinia";
import { ProvideKeys } from "@/utils/constants/provideKeys";
import { useUiStore } from "@/storePinia/ui";
import PersistentBanner from "@/components/PersistentBanner.vue";
import HeaderAlerts from "@/components/HeaderAlerts.vue";
import NoAccess from "@/components/ui/NoAccess.vue";
import {
  getLocalUser,
  hasManagerAccessToPath,
} from "@/constants/managerAccess";

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();
const uiStore = useUiStore();
const { isSidebarOpen } = storeToRefs(uiStore);
const { isMobile } = useBreakpoints();
const storeCompany = useCompanyStore();
const { company } = storeToRefs(storeCompany);
const scrollableContainer = useTemplateRef("scrollableContainer");

const COMPANY_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

useIntervalFn(() => {
  storeCompany.fetchCompany();
}, COMPANY_REFRESH_INTERVAL);

const isLoading = computed(() => {
  return props.isLoading;
});

const hasRouteAccess = computed(() => {
  return hasManagerAccessToPath(route.path, getLocalUser());
});

const navClasses = computed(() => {
  return {
    "position-sticky blur shadow-blur mt-4 left-auto top-1 z-index-sticky":
      store.state.isNavFixed,
    "position-absolute px-4 mx-0 w-100 z-index-2": store.state.isAbsolute,
    "px-0 mx-4 mt-4": store.state.isAbsolute,
  };
});

const showNavbar = computed(() => {
  return (
    store.state.showSidenav &&
    !(storeCompany.isEmptyTenant && !storeCompany.companyIsHotel) &&
    !isLoading.value
  );
});

const isCalendarMob = computed(() => {
  return (
    isMobile.value &&
    route.path === "/ecommerce/calendar" &&
    company.value?.crmMode !== "hotel"
  );
});

const pathsWithHiddenFooter = [
  "/ecommerce/calendar",
  "/team/workschedule",
  "/ecommerce/rooms-prices",
  "/ecommerce/restrictions",
  "/ecommerce/staff-rates",
  "/spa/resources-schedule",
];

const showFooter = computed(() => {
  if (pathsWithHiddenFooter.includes(route.path)) {
    return false;
  }

  return store.state.showFooter;
});

provide(ProvideKeys.scrollableContainer, scrollableContainer);
</script>

<template>
  <navbar
    v-if="showNavbar"
    :class="[navClasses]"
    class="w-100 position-fixed"
    :textWhite="store.state.isAbsolute ? 'text-white opacity-8' : ''"
  >
    <template #banner>
      <HeaderAlerts />
    </template>
  </navbar>

  <div v-if="showNavbar">
    <Transition name="sidenav">
      <Sidenav
        v-show="isSidebarOpen"
        :class="[store.state.isRTL ? 'fixed-end' : 'fixed-start']"
        :custom_class="store.state.mcolor"
      />
    </Transition>
  </div>

  <main
    ref="scrollableContainer"
    class="main-content d-flex flex-column position-relative d-height border-radius-lg overflow-x-hidden"
    :class="{
      'menu-opened': isSidebarOpen,
      'main-content--footer-gap': showFooter,
    }"
  >
    <PersistentBanner />
    <NoAccess v-if="!hasRouteAccess" />
    <router-view v-else :key="route.path" />

    <div v-if="isCalendarMob" class="mt-6"></div>
  </main>
</template>

<style>
.d-height {
  height: 100% !important;
  overflow-y: scroll;
}

@media screen and (min-width: 1025px) {
  .main-content.menu-opened {
    margin-left: var(--sidebar-width);
  }

  .main-content {
    padding-top: var(--header-height);
  }

  .main-content--footer-gap {
    padding-bottom: var(--content-padding-bottom);
  }
}

@media screen and (max-width: 1024.98px) {
  .main-content {
    padding-top: var(--header-height);
    padding-bottom: calc(var(--mobile-bottom-nav-clearance) + 8px);
  }
}

.g-sidenav-show .sidenav:not(.sidenav-enter-from):not(.sidenav-leave-to) {
  transform: translateX(0) !important;
}

.sidenav-enter-active,
.sidenav-leave-active {
  transition: transform 0.3s ease;
}

.sidenav-enter-from,
.sidenav-leave-to {
  transform: translateX(-100%) !important;
}
</style>
